<template>
    <div id="contentcontainter" class="standard-mode" style="padding-bottom: 85px">
        <!-- SNACKBAR -->
        <v-snackbar top v-model="notification.show" :color="notification.color" v-if="notification">
            <v-icon>{{ notification.icon }}</v-icon
            ><span class="font-weight-bold pl-2"> {{ notification.message }} </span>
        </v-snackbar>

        <!-- HERO -->
        <v-slide-x-transition>
            <div class="flex mt-10">
                <v-card class="mx-auto rounded_card quaroshadow seoaudit-hero" style="width: 100%">
                    <v-card-text>
                        <div class="seoaudit-hero__row">
                            <div class="seoaudit-hero__titles">
                                <h2 class="seoaudit-hero__title">Page Audit</h2>
                                <p class="seoaudit-hero__subtitle mb-0">Check how well your pages can be read, understood and cited by AI search engines.</p>
                            </div>
                            <div class="seoaudit-hero__actions">
                                <v-chip outlined color="#009f89" class="mr-3 seoaudit-pill--credits" v-if="credits">
                                    <v-icon small class="mr-1">mdi-lightning-bolt-outline</v-icon>
                                    {{ credits.audits }} / {{ credits.audits_limit }} audits used
                                </v-chip>
                                <v-btn rounded color="#009f89" dark depressed :disabled="!$store.getters.canWriteAiSuite" @click="openNewAuditDialog" class="seoaudit-btn">
                                    <v-icon class="mr-2">mdi-plus-circle-outline</v-icon>
                                    New Audit
                                </v-btn>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </v-slide-x-transition>

        <!-- RUNS TABLE -->
        <v-card class="mx-auto mt-5 rounded_card quaroshadow" style="width: 100%">
            <v-data-table
                :headers="headers"
                :items="runs"
                :loading="loading"
                :items-per-page="20"
                sort-by="createdAt"
                :sort-desc="true"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                class="elevation-0 seoaudit-table--clickable"
                @click:row="openRun">
                <template v-slot:item.name="{ item }">
                    <div class="seoaudit-table__namecell">
                        <span class="seoaudit-table__name">{{ item.name || "Audit" }}</span>
                        <div v-if="item.urls && item.urls.length" class="seoaudit-table__urls">
                            <v-icon x-small class="seoaudit-table__urlicon">mdi-link-variant</v-icon>
                            <span class="seoaudit-table__url" :title="item.urls[0]">{{ displayUrl(item.urls[0]) }}</span>
                            <span v-if="item.urls.length > 1" class="seoaudit-table__urlmore">+{{ item.urls.length - 1 }}</span>
                        </div>
                    </div>
                </template>
                <template v-slot:item.status="{ item }">
                    <v-chip small :color="statusColor(item.status)" class="white--text seoaudit-pill">{{ item.status }}</v-chip>
                </template>
                <template v-slot:item.progress="{ item }">
                    <div v-if="item.status === 'processing' || item.status === 'pending'" class="seoaudit-table__progress">
                        <v-progress-linear rounded height="14" color="primary" :value="progressPercent(item)">
                            <span class="seoaudit-table__progresstext">{{ progressText(item) }}</span>
                        </v-progress-linear>
                    </div>
                    <span v-else>{{ progressText(item) }}</span>
                </template>
                <template v-slot:item.score_avg="{ item }">
                    <v-chip v-if="item.score_avg !== null && item.score_avg !== undefined" small outlined :color="scoreColor(item.score_avg)" class="seoaudit-pill seoaudit-pill--outline">
                        {{ Math.round(item.score_avg) }}
                    </v-chip>
                    <span v-else>-</span>
                </template>
                <template v-slot:item.url_count="{ item }">
                    <v-chip small outlined color="#009f89" class="seoaudit-pill seoaudit-pill--outline">{{ item.url_count }}</v-chip>
                </template>
                <template v-slot:item.createdAt="{ item }">
                    <span style="white-space: nowrap">{{ formatDate(item.createdAt) }}</span>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div class="d-flex justify-end">
                        <v-btn icon @click.stop="openRun(item)" aria-label="Open audit">
                            <v-icon>mdi-open-in-app</v-icon>
                        </v-btn>
                        <v-btn icon :disabled="!$store.getters.canWriteAiSuite" @click.stop="askDeleteRun(item)" aria-label="Delete audit">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </template>
                <template v-slot:no-data>
                    <div class="py-6 grey--text">No audits yet. Start your first audit with the "New Audit" button.</div>
                </template>
            </v-data-table>
        </v-card>

        <!-- NEW AUDIT DIALOG -->
        <v-dialog v-model="newAuditDialog" max-width="700px" @click:outside="newAuditDialog = false">
            <v-card>
                <v-card-title class="text-h6">New Page Audit</v-card-title>
                <v-card-text>
                    <v-text-field v-model="newAudit.name" label="Name (optional)" dense outlined hide-details class="mb-4"></v-text-field>
                    <v-select
                        v-model="newAudit.project_id"
                        :items="projects"
                        item-text="title"
                        item-value="_id"
                        label="AI project (optional)"
                        :loading="projectsLoading"
                        dense
                        outlined
                        clearable
                        hide-details
                        class="mb-4"></v-select>
                    <v-textarea
                        v-model="newAudit.urlsText"
                        label="URLs — one per line"
                        placeholder="https://www.example.com/page-1&#10;https://www.example.com/page-2"
                        rows="8"
                        outlined
                        hide-details></v-textarea>
                    <div class="seoaudit-newdialog__meta mt-2">
                        <span :class="urlCountClass">{{ parsedUrls.valid.length }} / 50 URLs</span>
                        <span v-if="parsedUrls.invalid.length" class="error--text ml-3">
                            {{ parsedUrls.invalid.length }} invalid line{{ parsedUrls.invalid.length > 1 ? "s" : "" }} (http/https URLs only)
                        </span>
                    </div>
                    <div v-if="parsedUrls.invalid.length" class="seoaudit-newdialog__invalid mt-1">
                        <span v-for="(line, i) in parsedUrls.invalid.slice(0, 3)" :key="i" class="error--text d-block">{{ line }}</span>
                    </div>
                    <v-btn v-if="newAudit.project_id" small outlined color="#009f89" class="mt-3 seoaudit-btn seoaudit-btn--small" :loading="importLoading" @click="importCitedUrls">
                        <v-icon small class="mr-1">mdi-import</v-icon>
                        Import cited URLs
                    </v-btn>

                    <!-- FOCUS: what these pages should be found for -->
                    <v-divider class="my-4"></v-divider>
                    <v-textarea
                        v-model="newAudit.focusText"
                        label="Focus keywords or prompts (one per line)"
                        placeholder="laminate flooring installation&#10;Which laminate is suitable for the kitchen?"
                        rows="4"
                        outlined
                        hide-details></v-textarea>
                    <div class="seoaudit-newdialog__meta mt-2">
                        <span :class="focusCountClass">{{ parsedFocus.entries.length }} / 20 focus entries</span>
                        <span v-if="parsedFocus.duplicates" class="grey--text ml-3"> {{ parsedFocus.duplicates }} duplicate{{ parsedFocus.duplicates > 1 ? "s" : "" }} removed </span>
                        <span v-if="parsedFocus.tooLong" class="warning--text ml-3">
                            {{ parsedFocus.tooLong }} entr{{ parsedFocus.tooLong > 1 ? "ies are" : "y is" }} longer than 200 characters and will be shortened
                        </span>
                    </div>
                    <div class="seoaudit-newdialog__hint mt-1">What should these pages be found for? Suggestions and coverage are based on these.</div>
                    <v-btn v-if="newAudit.project_id" small outlined color="#009f89" class="mt-3 seoaudit-btn seoaudit-btn--small" @click="openPromptPicker">
                        <v-icon small class="mr-1">mdi-format-list-checks</v-icon>
                        Pick from tracked prompts
                    </v-btn>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn text rounded class="seoaudit-btn seoaudit-btn--ghost" @click="newAuditDialog = false">Cancel</v-btn>
                    <v-btn rounded color="#009f89" dark depressed class="ml-auto seoaudit-btn" :loading="creating" :disabled="!canSubmit" @click="submitNewAudit">
                        <v-icon class="mr-2">mdi-play-circle-outline</v-icon>
                        Start Audit
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- TRACKED PROMPT PICKER (focus entries) -->
        <v-dialog v-model="promptPickerDialog" max-width="640px" scrollable @click:outside="closePromptPicker">
            <v-card>
                <v-card-title class="text-h6">Pick from tracked prompts</v-card-title>
                <v-card-text class="pb-0">
                    <v-text-field v-model="promptSearch" label="Search prompts" prepend-inner-icon="mdi-magnify" dense outlined clearable hide-details class="mb-3"></v-text-field>
                    <div class="seoaudit-newdialog__meta mb-2">
                        <span class="grey--text">{{ promptCountLabel }}</span>
                        <span v-if="pickerSelected.length" class="primary--text ml-3">{{ pickerSelected.length }} selected</span>
                    </div>
                    <div v-if="promptsLoading" class="py-6 text-center">
                        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
                    </div>
                    <div v-else-if="!promptOptions.length" class="py-6 grey--text">No tracked prompts found for this project.</div>
                    <div v-else class="seoaudit-picker__list">
                        <div v-for="(prompt, i) in promptOptions" :key="'prompt-' + i" class="seoaudit-picker__row">
                            <v-checkbox
                                :input-value="pickerSelected.indexOf(prompt) > -1"
                                :disabled="isFocusEntry(prompt)"
                                :label="prompt"
                                dense
                                hide-details
                                class="mt-0 pt-0"
                                @change="togglePickerPrompt(prompt)"></v-checkbox>
                            <span v-if="isFocusEntry(prompt)" class="seoaudit-picker__added">Added</span>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn text rounded class="seoaudit-btn seoaudit-btn--ghost" @click="closePromptPicker">Cancel</v-btn>
                    <v-btn rounded color="#009f89" dark depressed class="ml-auto seoaudit-btn" :disabled="!pickerSelected.length" @click="applyPickedPrompts">
                        <v-icon class="mr-2">mdi-plus</v-icon>
                        Add {{ pickerSelected.length ? pickerSelected.length + " " : "" }}to focus
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- DELETE DIALOG -->
        <DeleteDialog
            :deleteDialog="deleteDialog"
            :selectedDeleteItem="selectedDeleteItem.name || 'Audit'"
            deleteLabel="Audit"
            :loading="deleting"
            @delete="deleteSelectedRun"
            @close="deleteDialog = false"></DeleteDialog>
    </div>
</template>

<script>
    import DeleteDialog from "../../components/dialogs/DeleteDialog.vue"
    import { resolveApiRoot, createRun, fetchRuns, deleteRun, suggestSources, suggestPrompts, fetchCredits } from "../../components/seoaudit/seoAuditApi"

    // focus entries the backend accepts per run (it caps at the same values)
    const MAX_FOCUS_ENTRIES = 20
    const MAX_FOCUS_LENGTH = 200
    // prompts requested for the picker (backend max is 200)
    const PROMPT_PICKER_LIMIT = 200
    // typing delay before the search hits the backend again
    const PROMPT_SEARCH_DEBOUNCE = 350

    export default {
        name: "SeoAudits",
        components: {
            DeleteDialog
        },
        data() {
            return {
                loading: false,
                runs: [],
                credits: null,
                notification: { show: false },
                pollInterval: null,
                // set in beforeDestroy so in-flight requests cannot restart polling afterwards
                isDestroyed: false,
                // new audit dialog
                newAuditDialog: false,
                creating: false,
                newAudit: { name: "", project_id: null, urlsText: "", focusText: "" },
                projects: [],
                projectsLoading: false,
                projectsLoaded: false,
                importLoading: false,
                // tracked-prompt picker for the focus field
                promptPickerDialog: false,
                promptSearch: "",
                promptOptions: [],
                promptTotal: 0,
                // search term the shown list belongs to ("" = unfiltered): a filtered list must not
                // be counted against the tracked total, and a failed request clears both
                promptListSearch: "",
                promptsLoading: false,
                pickerSelected: [],
                // debounce timer of the picker search — cleared on close and on destroy
                promptSearchTimer: null,
                // true while the search field is being reset programmatically (opening the picker):
                // the watcher must not turn that reset into a second, identical request
                promptSearchSuppressed: false,
                // bumped per request; a response with a stale token belongs to an abandoned search
                promptRequestToken: 0,
                // delete dialog
                deleteDialog: false,
                deleting: false,
                selectedDeleteItem: {},
                headers: [
                    { text: "Name", value: "name", sortable: true },
                    { text: "Status", value: "status", sortable: true },
                    { text: "Progress", value: "progress", sortable: false, width: 180 },
                    { text: "Ø Score", value: "score_avg", sortable: true },
                    { text: "URLs", value: "url_count", sortable: true },
                    { text: "Created", value: "createdAt", sortable: true },
                    { text: "", value: "actions", sortable: false, align: "end", width: 110 }
                ]
            }
        },
        computed: {
            parsedUrls() {
                const lines = (this.newAudit.urlsText || "")
                    .split("\n")
                    .map((l) => l.trim())
                    .filter((l) => l.length)
                const valid = []
                const invalid = []
                const seen = new Set()
                lines.forEach((line) => {
                    let ok = false
                    try {
                        const u = new URL(line)
                        ok = u.protocol === "http:" || u.protocol === "https:"
                    } catch (e) {
                        ok = false
                    }
                    if (!ok) {
                        invalid.push(line)
                        return
                    }
                    if (!seen.has(line)) {
                        seen.add(line)
                        valid.push(line)
                    }
                })
                return { valid, invalid }
            },
            urlCountClass() {
                if (this.parsedUrls.valid.length > 50) return "error--text font-weight-bold"
                return this.parsedUrls.valid.length ? "primary--text" : "grey--text"
            },
            // focus entries: internal whitespace collapsed, empties dropped, deduplicated exactly
            // like the backend does (see sanitizeFocusList) — otherwise the count shown here can be
            // larger than the number of entries the run actually stores.
            // Over-long lines are counted but kept (the backend shortens them to 200 chars).
            parsedFocus() {
                const lines = (this.newAudit.focusText || "")
                    .split("\n")
                    .map((l) => l.replace(/\s+/g, " ").trim())
                    .filter((l) => l.length)
                const entries = []
                const seen = new Set()
                let duplicates = 0
                let tooLong = 0
                lines.forEach((line) => {
                    const key = this.focusKey(line)
                    if (seen.has(key)) {
                        duplicates++
                        return
                    }
                    seen.add(key)
                    if (line.length > MAX_FOCUS_LENGTH) tooLong++
                    entries.push(line)
                })
                return { entries, duplicates, tooLong }
            },
            // "N results" while a search filters the list — the tracked total says nothing about
            // a filtered list and would read as if prompts were missing from it
            promptCountLabel() {
                const shown = this.promptOptions.length
                if (this.promptListSearch) return shown + " result" + (shown === 1 ? "" : "s")
                let label = shown + " shown"
                if (this.promptTotal > shown) label += " of " + this.promptTotal + " tracked prompts"
                return label
            },
            focusCountClass() {
                if (this.parsedFocus.entries.length > MAX_FOCUS_ENTRIES) return "error--text font-weight-bold"
                return this.parsedFocus.entries.length ? "primary--text" : "grey--text"
            },
            // normalized focus entries — used to grey out prompts that are already in the field
            focusKeys() {
                return this.parsedFocus.entries.map((entry) => this.focusKey(entry))
            },
            canSubmit() {
                return (
                    this.parsedUrls.valid.length > 0 &&
                    this.parsedUrls.valid.length <= 50 &&
                    this.parsedUrls.invalid.length === 0 &&
                    this.parsedFocus.entries.length <= MAX_FOCUS_ENTRIES &&
                    !this.creating
                )
            }
        },
        methods: {
            statusColor(status) {
                const map = { pending: "#8fbdb8", processing: "#009f89", done: "#009f89", failed: "#d94040" }
                return map[status] || "#8fbdb8"
            },
            scoreColor(score) {
                if (score >= 80) return "#009f89"
                if (score >= 60) return "#e5a400"
                return "#d94040"
            },
            progressPercent(run) {
                const total = run.progress && run.progress.total ? run.progress.total : 0
                const done = run.progress && run.progress.done ? run.progress.done : 0
                return total ? Math.round((done / total) * 100) : 0
            },
            progressText(run) {
                const total = run.progress && run.progress.total ? run.progress.total : run.url_count || 0
                const done = run.progress && run.progress.done ? run.progress.done : 0
                return done + "/" + total
            },
            formatDate(date) {
                if (!date) return "-"
                const d = new Date(date)
                if (isNaN(d)) return "-"
                return d.toISOString().slice(0, 10)
            },
            openRun(run) {
                this.$router.push("/ai-suite/audit/run/" + run._id)
            },
            displayUrl(url) {
                if (!url) return ""
                try {
                    const u = new URL(url)
                    const path = u.pathname === "/" ? "" : u.pathname.replace(/\/$/, "")
                    return u.hostname.replace(/^www\./, "") + path
                } catch (e) {
                    return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")
                }
            },
            async fetchRunsList(silent = false) {
                try {
                    if (!silent) this.loading = true
                    const runs = await fetchRuns(localStorage.company_id)
                    // component was destroyed while the request was in flight
                    if (this.isDestroyed) return
                    this.runs = runs
                    this.updatePolling()
                } catch (error) {
                    if (this.isDestroyed) return
                    console.error("fetchRunsList error", error)
                    if (!silent) {
                        this.notification = this.$helpers.createNotification("Error loading audits. Please try again later.", "mdi-alert-circle-outline", "error")
                    }
                } finally {
                    if (!silent) this.loading = false
                }
            },
            async fetchCreditsInfo() {
                try {
                    const data = await fetchCredits(localStorage.company_id)
                    if (data && data.audits !== undefined) {
                        this.credits = { audits: data.audits, audits_limit: data.audits_limit }
                    }
                } catch (error) {
                    console.error("fetchCreditsInfo error", error)
                }
            },
            updatePolling() {
                if (this.isDestroyed) return
                const active = this.runs.some((r) => r.status === "pending" || r.status === "processing")
                if (active && !this.pollInterval) {
                    this.pollInterval = setInterval(() => {
                        this.fetchRunsList(true)
                    }, 5000)
                } else if (!active && this.pollInterval) {
                    this.stopPolling()
                }
            },
            stopPolling() {
                if (this.pollInterval) {
                    clearInterval(this.pollInterval)
                    this.pollInterval = null
                }
            },
            openNewAuditDialog() {
                if (!this.$store.getters.canWriteAiSuite) return
                this.newAudit = { name: "", project_id: null, urlsText: "", focusText: "" }
                this.newAuditDialog = true
                if (!this.projectsLoaded) this.fetchProjects()
            },
            // deep link: /ai-suite/audit?project=<id>&urls=<url>&focus=<prompt> opens the dialog
            // prefilled (used by the "Audit a page for this prompt" buttons in the AI Suite).
            // "urls" and "focus" may be repeated (?focus=a&focus=b) or newline separated; URLs may
            // additionally be comma separated. Nothing here is trusted: the project must be one of
            // the account's own, everything else is validated by the dialog exactly like typed input.
            async applyRouteQuery() {
                const query = (this.$route && this.$route.query) || {}
                const project = typeof query.project === "string" ? query.project.trim() : ""
                const urls = this.queryLines(query.urls, /\n|,(?=\s*https?:\/\/)/)
                const focus = this.queryLines(query.focus, /\n/)
                if (!project && !urls.length && !focus.length) return
                this.newAudit = { name: "", project_id: null, urlsText: urls.slice(0, 50).join("\n"), focusText: focus.slice(0, MAX_FOCUS_ENTRIES).join("\n") }
                this.newAuditDialog = true
                // the query has done its job: a reload or the browser back button must not reopen the dialog
                this.$router.replace({ path: this.$route.path }).catch(() => {})
                if (!this.projectsLoaded) await this.fetchProjects()
                if (this.isDestroyed || !project) return
                if (this.projects.some((p) => String(p._id) === project)) {
                    this.newAudit.project_id = project
                } else if (this.projectsLoaded) {
                    // the list did load, the id is just not in it (other account, deleted, wrong type)
                    this.notification = this.$helpers.createNotification("The linked project was not found. Please pick one from the list.", "mdi-alert-circle-outline", "warning")
                }
            },
            queryLines(value, separator) {
                const parts = Array.isArray(value) ? value : [value]
                const lines = []
                parts.forEach((part) => {
                    if (typeof part !== "string") return
                    part.split(separator).forEach((line) => {
                        const trimmed = line.trim()
                        if (trimmed.length) lines.push(trimmed)
                    })
                })
                return lines
            },
            // ---- tracked prompt picker ----
            // the key the backend compares on: internal whitespace collapsed, trimmed, cut to the
            // stored length, case-insensitive (see sanitizeFocusList in seoAuditFunctions.js)
            focusKey(value) {
                return String(value == null ? "" : value)
                    .replace(/\s+/g, " ")
                    .trim()
                    .slice(0, MAX_FOCUS_LENGTH)
                    .toLowerCase()
            },
            isFocusEntry(prompt) {
                return this.focusKeys.indexOf(this.focusKey(prompt)) > -1
            },
            // a project can track the same prompt text several times; two rows for one text would
            // share a single checkbox state, so the list is deduplicated before it is rendered
            dedupePrompts(prompts) {
                const seen = new Set()
                const out = []
                ;(Array.isArray(prompts) ? prompts : []).forEach((prompt) => {
                    const key = this.focusKey(prompt)
                    if (!key || seen.has(key)) return
                    seen.add(key)
                    out.push(prompt)
                })
                return out
            },
            openPromptPicker() {
                if (!this.newAudit.project_id) return
                this.clearPromptSearchTimer()
                // clearing the field fires the search watcher, which would send a second request
                // identical to the one below — suppress it, but only when it really fires
                this.promptSearchSuppressed = this.promptSearch !== ""
                this.promptSearch = ""
                this.pickerSelected = []
                this.promptOptions = []
                this.promptTotal = 0
                this.promptListSearch = ""
                this.promptPickerDialog = true
                this.fetchPrompts()
            },
            closePromptPicker() {
                this.clearPromptSearchTimer()
                this.promptPickerDialog = false
                this.pickerSelected = []
            },
            clearPromptSearchTimer() {
                if (this.promptSearchTimer) {
                    clearTimeout(this.promptSearchTimer)
                    this.promptSearchTimer = null
                }
            },
            togglePickerPrompt(prompt) {
                const idx = this.pickerSelected.indexOf(prompt)
                if (idx > -1) this.pickerSelected.splice(idx, 1)
                else this.pickerSelected.push(prompt)
            },
            async fetchPrompts() {
                if (!this.newAudit.project_id) return
                const token = ++this.promptRequestToken
                // declared outside the try: the error path needs it to label the empty list
                const search = (this.promptSearch || "").trim()
                try {
                    this.promptsLoading = true
                    const data = await suggestPrompts(localStorage.company_id, {
                        projectId: this.newAudit.project_id,
                        search: search || undefined,
                        limit: PROMPT_PICKER_LIMIT
                    })
                    // component gone or a newer search already running -> drop this response
                    if (this.isDestroyed || token !== this.promptRequestToken) return
                    this.promptOptions = this.dedupePrompts(data.prompts)
                    this.promptTotal = data.total
                    this.promptListSearch = search
                    // selections survive a search change on purpose: pick a few, search again, pick more
                } catch (error) {
                    if (this.isDestroyed || token !== this.promptRequestToken) return
                    console.error("fetchPrompts error", error)
                    this.promptOptions = []
                    // the previous total belongs to a list that is no longer shown
                    this.promptTotal = 0
                    this.promptListSearch = search
                    this.notification = this.$helpers.createNotification("Error loading tracked prompts.", "mdi-alert-circle-outline", "error")
                } finally {
                    if (token === this.promptRequestToken) this.promptsLoading = false
                }
            },
            applyPickedPrompts() {
                const additions = this.pickerSelected.filter((prompt) => !this.isFocusEntry(prompt))
                if (!additions.length) {
                    this.closePromptPicker()
                    return
                }
                const current = (this.newAudit.focusText || "").trim()
                this.newAudit.focusText = (current ? current + "\n" : "") + additions.join("\n")
                this.notification = this.$helpers.createNotification("Added " + additions.length + " prompt" + (additions.length > 1 ? "s" : "") + " to the focus.", "mdi-check-circle", "primary")
                this.closePromptPicker()
            },
            async fetchProjects() {
                // Projekte werden über denselben Endpunkt wie in AIProjects.vue geladen
                try {
                    this.projectsLoading = true
                    const url = resolveApiRoot(localStorage.company_id) + "/ai-suite/projects/get"
                    const response_data = await this.$helpers.axiosPost(url, { company_id: localStorage.company_id })
                    const projects = response_data?.data?.projects || []
                    this.projects = projects.filter((p) => p.type == "ai_tracking")
                    this.projectsLoaded = true
                } catch (error) {
                    console.error("fetchProjects error", error)
                } finally {
                    this.projectsLoading = false
                }
            },
            async importCitedUrls() {
                if (!this.newAudit.project_id) return
                try {
                    this.importLoading = true
                    const sources = await suggestSources(localStorage.company_id, { projectId: this.newAudit.project_id })
                    const existing = new Set(this.parsedUrls.valid)
                    const additions = sources.map((s) => s.url).filter((u) => u && !existing.has(u))
                    if (!additions.length) {
                        this.notification = this.$helpers.createNotification("No new cited URLs found for this project.", "mdi-information", "info")
                        return
                    }
                    const current = (this.newAudit.urlsText || "").trim()
                    this.newAudit.urlsText = (current ? current + "\n" : "") + additions.join("\n")
                    this.notification = this.$helpers.createNotification("Added " + additions.length + " cited URL" + (additions.length > 1 ? "s" : "") + ".", "mdi-check-circle", "primary")
                } catch (error) {
                    console.error("importCitedUrls error", error)
                    this.notification = this.$helpers.createNotification("Error loading cited URLs.", "mdi-alert-circle-outline", "error")
                } finally {
                    this.importLoading = false
                }
            },
            async submitNewAudit() {
                if (!this.canSubmit) return
                try {
                    this.creating = true
                    const data = await createRun(localStorage.company_id, {
                        urls: this.parsedUrls.valid,
                        name: this.newAudit.name ? this.newAudit.name.trim() : null,
                        projectId: this.newAudit.project_id,
                        focus: this.parsedFocus.entries
                    })
                    if (data && data.error) {
                        // backend signals a hit credit limit with HTTP 200 and
                        // { error: "not_enough_credits", credits: <limit>, count: <used>, credits_needed: <n> }
                        // ("not enough credits" is the legacy spelling of the error code)
                        if (data.error === "not_enough_credits" || data.error === "not enough credits") {
                            const available = Math.max(0, (Number(data.credits) || 0) - (Number(data.count) || 0))
                            const needed = Number(data.credits_needed) || 0
                            this.notification = this.$helpers.createNotification("Not enough audit credits: " + available + " available, " + needed + " needed.", "mdi-alert-circle-outline", "error")
                        } else {
                            this.notification = this.$helpers.createNotification(data.error, "mdi-alert-circle-outline", "error")
                        }
                        return
                    }
                    if (data && data.status === "started" && data.run_id) {
                        this.newAuditDialog = false
                        this.notification = this.$helpers.createNotification("Audit started!", "mdi-check-circle", "primary")
                        this.$router.push("/ai-suite/audit/run/" + data.run_id)
                        return
                    }
                    this.notification = this.$helpers.createNotification("Unexpected response. Please try again later.", "mdi-alert-circle-outline", "error")
                } catch (error) {
                    console.error("submitNewAudit error", error)
                    const message = error?.response?.data?.error || "Error starting the audit. Please try again later."
                    this.notification = this.$helpers.createNotification(message, "mdi-alert-circle-outline", "error")
                } finally {
                    this.creating = false
                }
            },
            askDeleteRun(run) {
                if (!this.$store.getters.canWriteAiSuite) return
                this.selectedDeleteItem = run
                this.deleteDialog = true
            },
            async deleteSelectedRun() {
                if (!this.selectedDeleteItem._id) return
                try {
                    this.deleting = true
                    await deleteRun(localStorage.company_id, this.selectedDeleteItem._id)
                    const idx = this.runs.findIndex((r) => r._id === this.selectedDeleteItem._id)
                    if (idx > -1) this.runs.splice(idx, 1)
                    this.notification = this.$helpers.createNotification("Audit deleted.", "mdi-check-circle", "primary")
                } catch (error) {
                    console.error("deleteSelectedRun error", error)
                    const message = error?.response?.data?.error || "Error deleting the audit."
                    this.notification = this.$helpers.createNotification(message, "mdi-alert-circle-outline", "error")
                } finally {
                    this.deleting = false
                    this.deleteDialog = false
                    this.selectedDeleteItem = {}
                }
            }
        },
        watch: {
            // debounced server-side search of the prompt picker (clearable field emits null)
            promptSearch() {
                // programmatic reset while opening the picker — that request is already on its way
                if (this.promptSearchSuppressed) {
                    this.promptSearchSuppressed = false
                    return
                }
                if (!this.promptPickerDialog) return
                this.clearPromptSearchTimer()
                this.promptSearchTimer = setTimeout(() => {
                    this.promptSearchTimer = null
                    if (this.isDestroyed || !this.promptPickerDialog) return
                    this.fetchPrompts()
                }, PROMPT_SEARCH_DEBOUNCE)
            }
        },
        mounted() {
            this.fetchRunsList()
            this.fetchCreditsInfo()
            this.applyRouteQuery()
        },
        beforeDestroy() {
            this.isDestroyed = true
            this.stopPolling()
            this.clearPromptSearchTimer()
        }
    }
</script>

<style lang="scss" scoped>
    @import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

    #contentcontainter {
        font-family: "Plus Jakarta Sans", system-ui, -apple-system, sans-serif;
        color: #0d2622;
        background: #f2faf8;
    }
    ::v-deep .v-btn {
        text-transform: none;
        letter-spacing: normal;
        font-family: inherit;
        font-weight: 600;
        box-shadow: none !important;
    }
    ::v-deep .v-card {
        border: 1px solid #d4ede9;
        border-radius: 16px !important;
        box-shadow: 0 1px 4px rgba(0, 159, 137, 0.08), 0 4px 16px rgba(0, 0, 0, 0.06) !important;
    }
    .seoaudit-btn.v-btn--outlined {
        border-color: #d4ede9;
    }
    .seoaudit-btn--ghost {
        color: #5a8c84 !important;
    }
    .seoaudit-pill {
        border-radius: 999px !important;
        font-weight: 600;
        height: 28px !important;
        font-size: 13px !important;
        padding: 0 12px !important;
    }
    .seoaudit-pill--outline.v-chip.v-chip--outlined {
        background: #ffffff;
        border-width: 1.5px !important;
    }
    .seoaudit-hero__row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
    }
    .seoaudit-hero__titles {
        flex: 1 1 auto;
        min-width: 240px;
    }
    .seoaudit-hero__title {
        margin-bottom: 4px;
        font-size: 22px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: #0d2622;
    }
    .seoaudit-hero__subtitle {
        color: #5a8c84;
    }
    .seoaudit-hero__actions {
        display: flex;
        align-items: center;
    }
    .seoaudit-table__name {
        cursor: pointer;
        font-weight: 500;
        color: #0d2622;
    }
    .seoaudit-table__name:hover {
        color: #009f89;
    }
    .seoaudit-table__namecell {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 4px 0;
    }
    .seoaudit-table__urls {
        display: flex;
        align-items: center;
        gap: 4px;
        max-width: 320px;
    }
    .seoaudit-table__urlicon {
        color: #9aa8a4 !important;
    }
    .seoaudit-table__url {
        font-size: 12px;
        color: #7a8a86;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .seoaudit-table__urlmore {
        font-size: 11px;
        font-weight: 600;
        color: #009f89;
        background: #e6f6f2;
        border-radius: 999px;
        padding: 1px 7px;
        flex: 0 0 auto;
    }
    .seoaudit-table--clickable ::v-deep tbody tr {
        cursor: pointer;
    }
    .seoaudit-table--clickable ::v-deep tbody tr:hover {
        background-color: #f4faf8;
    }
    .seoaudit-pill--credits {
        border-radius: 999px !important;
        height: 34px !important;
        font-size: 13px !important;
        font-weight: 600;
        padding: 0 14px !important;
        background: #ffffff;
        border-width: 1.5px !important;
    }
    .seoaudit-pill--credits ::v-deep .v-chip__content {
        height: 100%;
    }
    ::v-deep table > thead > tr > th {
        height: 46px !important;
        padding: 0 16px !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #5a8c84 !important;
    }
    ::v-deep table > tbody > tr > td {
        height: 64px !important;
        padding: 0 16px !important;
        vertical-align: middle;
    }
    ::v-deep table > tbody > tr:hover {
        background: #f2faf8 !important;
    }
    ::v-deep table > tbody > tr > td {
        font-size: 14px;
        color: #0d2622;
    }
    .seoaudit-table__progress {
        min-width: 140px;
    }
    .seoaudit-table__progresstext {
        font-size: 11px;
        color: white;
    }
    .seoaudit-newdialog__meta {
        font-size: 13px;
    }
    .seoaudit-newdialog__invalid {
        font-size: 12px;
        max-height: 60px;
        overflow-y: auto;
    }
    .seoaudit-newdialog__hint {
        font-size: 12px;
        color: #5a8c84;
        line-height: 1.45;
    }
    .seoaudit-picker__list {
        max-height: 320px;
        overflow-y: auto;
    }
    .seoaudit-picker__row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 2px 0;
    }
    .seoaudit-picker__row ::v-deep .v-label {
        font-size: 13px;
        line-height: 1.4;
    }
    .seoaudit-picker__added {
        flex: 0 0 auto;
        font-size: 11px;
        font-weight: 600;
        color: grey;
    }
</style>
